import { useAuth0 } from '@auth0/auth0-react';
import {
  ArrowRightIcon,
  CalendarIcon,
  ChevronDownIcon,
  MapPinIcon,
  MoonIcon,
} from '@heroicons/react/24/solid';
import {
  resolveBookingIntentSource,
  retainBookingIntentSource,
} from 'hotel/bookingIntentLineage';
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'hotel/navigation';
import { useHotelScope } from 'hotel/scope';
import { useEffect, useMemo, useRef, useState } from 'react';

import { TealButton } from 'components/Button';
import { HotelSearchQuery, useHotelSearchLazyQuery } from 'generated/graphql';
import {
  setSearchParams,
  reset as resetGroupDetails,
} from 'store/groupDetailsSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import { calculateHotelNights } from 'utilities/calculateHotelNights';
import { calculateGuestsPerRoom } from 'utilities/guestQuantityHelpers';

import HotelSearchMap from './HotelSearchMap';

interface SearchVariables {
  radius: number;
  latitude: number;
  longitude: number;
  checkInDate: string;
  checkOutDate: string;
  roomQuantity: number;
  guestQuantity: number;
  accessibleRoomsOnly?: boolean;
  description?: string;
}

/**
 * Helper function to transform search variables to API variables based on booking type
 * @param searchVariables - The search variables in frontend format (with total guests)
 * @param bookingType - The booking type ('group' or 'instant')
 * @returns API variables with guests per room and room quantity adjusted for booking type
 */
const transformSearchVariablesToApiVariables = (
  searchVariables: SearchVariables,
  bookingType: string,
) => {
  return {
    ...searchVariables,
    guestQuantity:
      bookingType === 'group'
        ? 1
        : calculateGuestsPerRoom(
            searchVariables.guestQuantity,
            searchVariables.roomQuantity,
          ),
    roomQuantity: bookingType === 'group' ? 1 : searchVariables.roomQuantity,
  };
};

export interface HotelSearchContext {
  data: HotelSearchQuery | undefined;
  loading: boolean;
  handleRefetch: (
    filters: Pick<SearchVariables, 'radius' | 'accessibleRoomsOnly'>,
  ) => void;
  handleFetchMore: () => void;
  handleHotelSelect: (
    result: HotelSearchQuery['hotelSearch']['results'][number],
  ) => void;
  highlightedHotelId: string | null;
  highlightHotel: (hotelId: string | null) => void;
}

interface HotelSearchInfoBarProps {
  description: string;
  checkInDate: string;
  checkOutDate: string;
  onReset: () => void;
  bookingType: string;
}

const HotelSearchInfoBar = ({
  description,
  checkInDate,
  checkOutDate,
  onReset,
  bookingType,
}: HotelSearchInfoBarProps) => {
  const count = useAppSelector((state) => state.groupDetails.shortList.length);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const numOfNights = calculateHotelNights(checkInDate, checkOutDate);
  const [isOpen, setIsOpen] = useState(false);

  const navToShortList = () => {
    dispatch(setSearchParams(searchParams.toString()));
    navigate('/shortlist');
  };

  return (
    <div className="flex flex-col border-b border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between p-4 lg:hidden">
        <p className="font-oswald text-lg font-bold text-gray-900">Your trip</p>
        <div className="flex items-center gap-2">
          {bookingType === 'group' && (
            <div className="flex items-center gap-1.5">
              <span className="text-base font-medium text-gray-500">
                Shortlist
              </span>
              <div className="flex size-6 items-center justify-center rounded-full bg-accentGreen text-sm font-medium text-white">
                {count}
              </div>
            </div>
          )}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-3 transition-colors hover:bg-gray-100"
          >
            <ChevronDownIcon
              className={`size-5 text-gray-900 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
      </div>

      <div className="hidden items-center justify-between p-4 lg:flex">
        <div className="flex items-center gap-8">
          <p className="font-oswald text-lg font-bold text-gray-900">
            Trip Details
          </p>
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <MapPinIcon className="size-5 text-accentGreen" />
              <p className="font-arimo text-base font-medium text-gray-900">
                {description}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <CalendarIcon className="size-5 text-accentGreen" />
              <p className="font-arimo text-base font-medium text-gray-900">
                {checkInDate}
              </p>
              <ArrowRightIcon className="size-5 text-gray-900" />
              <p className="font-arimo text-base font-medium text-gray-900">
                {checkOutDate}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <MoonIcon className="size-5 text-accentGreen" />
              <p className="font-arimo text-base font-medium text-gray-900">
                {numOfNights} nights
              </p>
            </div>

            <button
              onClick={onReset}
              className="rounded-md border border-gray-500 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Reset
            </button>
          </div>
        </div>

        {bookingType === 'group' && (
          <div className="flex items-center gap-4">
            <TealButton onClick={navToShortList}>
              View Shortlist ({count})
            </TealButton>
          </div>
        )}
      </div>

      {/* Accordion content */}
      <div
        className={`overflow-hidden transition-all duration-200 lg:hidden ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <div className="space-y-4 border-t border-gray-200 p-4">
          <div className="flex items-center gap-2">
            <MapPinIcon className="size-5 text-accentGreen" />
            <p className="font-arimo text-base font-medium text-gray-900">
              {description}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <CalendarIcon className="size-5 text-accentGreen" />
            <p className="font-arimo text-base font-medium text-gray-900">
              {checkInDate}
            </p>
            <ArrowRightIcon className="size-5 text-gray-900" />
            <p className="font-arimo text-base font-medium text-gray-900">
              {checkOutDate}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <MoonIcon className="size-5 text-accentGreen" />
            <p className="font-arimo text-base font-medium text-gray-900">
              {numOfNights} nights
            </p>
          </div>

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              onClick={onReset}
              className="w-full rounded-md border border-gray-500 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:w-auto"
            >
              Reset
            </button>
            {bookingType === 'group' && (
              <TealButton onClick={navToShortList} className="w-full sm:w-auto">
                View Shortlist ({count})
              </TealButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const HotelSearch = () => {
  const bookingType = useAppSelector((state) => state.booking.bookingType);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth0();
  const hotelScope = useHotelScope();
  const [searchParams] = useSearchParams();
  const [searchVariables, setSearchVariables] =
    useState<SearchVariables | null>(null);
  const { hotelId } = useParams<{ hotelId: string }>();

  const navigationToken = JSON.stringify([
    location.key,
    user?.sub,
    hotelScope?.clientId,
    hotelScope?.stateKey,
  ]);
  const currentNavigation = useRef<string | null>(navigationToken);
  currentNavigation.current = navigationToken;
  useEffect(() => {
    currentNavigation.current = navigationToken;
    return () => {
      currentNavigation.current = null;
    };
  }, [navigationToken]);

  const lineageScope = {
    actorId: user?.sub ?? '',
    clientId: hotelScope?.clientId ?? '',
  };
  const routeState = location.state as {
    bookingSourceKey?: string | null;
  } | null;
  let bookingSourceKey: string | null = null;
  try {
    bookingSourceKey = resolveBookingIntentSource(
      lineageScope,
      location.key,
      routeState?.bookingSourceKey,
    );
  } catch {
    // Search stays available; a missing lineage must not authorize a new Book.
  }

  useEffect(() => {
    if (location.key === 'default') {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: location.state ?? {},
      });
    }
  }, [
    location.key,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    if (bookingSourceKey) {
      void retainBookingIntentSource(
        { actorId: user?.sub ?? '', clientId: hotelScope?.clientId ?? '' },
        location.key,
        bookingSourceKey,
      ).catch(() => undefined);
    }
  }, [
    bookingSourceKey,
    hotelScope?.clientId,
    hotelScope?.stateKey,
    location.key,
    user?.sub,
  ]);

  const sourceForNavigation = async () => {
    if (!bookingSourceKey) return null;
    try {
      await retainBookingIntentSource(
        lineageScope,
        location.key,
        bookingSourceKey,
      );
      return bookingSourceKey;
    } catch {
      return null;
    }
  };

  const [executeHotelSearch, { loading, refetch, data, fetchMore }] =
    useHotelSearchLazyQuery({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-first',
    });

  const [highlightedHotelId, setHighlightedHotelId] = useState<string | null>(
    null,
  );

  const highlightHotel = (hotelId: string | null) => {
    setHighlightedHotelId(hotelId);
  };

  const getInfoValuesFromURL = () => {
    const { checkInDate, checkOutDate, guestQuantity, description } =
      Object.fromEntries(searchParams.entries());

    return {
      description: description || '',
      checkInDate,
      checkOutDate,
      guestQuantity,
    };
  };

  const resetGroupSearch = () => {
    // Reset group details including room configurations
    dispatch(resetGroupDetails());
    navigate(`/dashboard?${searchParams.toString()}`);
  };

  const updateURLWithSearchVariables = async (variables: SearchVariables) => {
    const params: Record<string, string> = {
      latitude: variables.latitude.toString(),
      longitude: variables.longitude.toString(),
      checkInDate: variables.checkInDate,
      checkOutDate: variables.checkOutDate,
      roomQuantity: variables.roomQuantity.toString(),
      guestQuantity: variables.guestQuantity.toString(),
      radius: variables.radius.toString(),
    };

    if (variables.description) {
      params.description = variables.description;
    }
    if (variables.accessibleRoomsOnly) {
      params.accessibleRoomsOnly = 'true';
    }
    const sourceKey = await sourceForNavigation();
    if (currentNavigation.current !== navigationToken) return;
    // Use scoped navigation so route metadata survives filter history entries.
    navigate(`${location.pathname}?${new URLSearchParams(params)}`, {
      state: { ...location.state, bookingSourceKey: sourceKey },
    });
  };

  useEffect(() => {
    try {
      const {
        latitude,
        longitude,
        checkInDate,
        checkOutDate,
        roomQuantity,
        guestQuantity,
        radius,
      } = Object.fromEntries(searchParams.entries());

      if (
        latitude &&
        longitude &&
        checkInDate &&
        checkOutDate &&
        roomQuantity &&
        guestQuantity
      ) {
        // Create search variables in frontend format (treating guestQuantity as totalGuests)
        const searchVariables: SearchVariables = {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          checkInDate,
          checkOutDate,
          roomQuantity: parseInt(roomQuantity, 10),
          guestQuantity: parseInt(guestQuantity, 10), // This is total guests from URL
          radius: radius ? parseInt(radius, 10) : 1,
          accessibleRoomsOnly:
            searchParams.get('accessibleRoomsOnly') === 'true',
          description: searchParams.get('description') || undefined,
        };

        setSearchVariables(searchVariables);

        // Create API variables, converting to guests per room when needed
        const apiVariables = transformSearchVariablesToApiVariables(
          searchVariables,
          bookingType,
        );

        executeHotelSearch({ variables: apiVariables });
      }
    } catch {
      // Error executing search from URL - handled silently
    }
  }, [searchParams, executeHotelSearch, bookingType]);

  const handleRefetch = (
    filters: Pick<SearchVariables, 'radius' | 'accessibleRoomsOnly'>,
  ) => {
    if (searchVariables) {
      const newVariables = { ...searchVariables, ...filters };

      const apiVariables = transformSearchVariablesToApiVariables(
        newVariables,
        bookingType,
      );

      setSearchVariables(newVariables);
      updateURLWithSearchVariables(newVariables);
      refetch(apiVariables);
    }
  };

  const handleFetchMore = () => {
    if (data?.hotelSearch?.moreIndicator) {
      fetchMore({
        variables: {
          moreIndicator: data.hotelSearch.moreIndicator,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;

          return {
            ...fetchMoreResult,
            hotelSearch: {
              ...fetchMoreResult.hotelSearch,
              results: [
                ...previousResult.hotelSearch.results,
                ...fetchMoreResult.hotelSearch.results,
              ],
            },
          };
        },
      });
    }
  };

  const handleHotelSelect = async (
    result: HotelSearchQuery['hotelSearch']['results'][number],
  ) => {
    const sourceKey = await sourceForNavigation();
    if (currentNavigation.current !== navigationToken) return;
    navigate(
      `/search/${result.hotel.amadeusHotelId}?${searchParams.toString()}`,
      {
        state: {
          bookingSourceKey: sourceKey,
          hotel: result.hotel,
          offers: result.offers,
          checkInDate: searchParams.get('checkInDate'),
          checkOutDate: searchParams.get('checkOutDate'),
          roomQuantity: searchParams.get('roomQuantity'),
          guestQuantity: searchParams.get('guestQuantity'),
          latitude: searchParams.get('latitude'),
          longitude: searchParams.get('longitude'),
          description: searchParams.get('description'),
          accessibleRoomsOnly: searchParams.get('accessibleRoomsOnly'),
          bookingType: searchParams.get('bookingType'),
        },
      },
    );
  };

  const memoizedResults = useMemo(() => {
    return (
      data?.hotelSearch?.results
        .filter((result) => {
          return hotelId ? result.hotel.amadeusHotelId === hotelId : true;
        })
        .map((result) => result.hotel) || []
    );
  }, [data?.hotelSearch?.results, hotelId]);

  const contextValue: HotelSearchContext = {
    data,
    loading,
    handleRefetch,
    handleFetchMore,
    handleHotelSelect,
    highlightedHotelId,
    highlightHotel,
  };

  return (
    <div
      className="flex w-full flex-col"
      style={{ minHeight: 'calc(100vh - 64px)' }}
    >
      <HotelSearchInfoBar
        {...getInfoValuesFromURL()}
        onReset={resetGroupSearch}
        bookingType={bookingType}
      />
      <div
        className="flex w-full flex-1 lg:flex-none"
        style={{ height: 'calc(100vh - 64px - 64px)' }}
      >
        <div className="relative size-full xl:z-10 xl:w-2/3 xl:border-r xl:border-gray-200/50 xl:bg-white xl:shadow-2xl xl:shadow-black/10">
          <Outlet context={contextValue} />
        </div>
        <div className="hidden h-full xl:relative xl:block xl:w-1/3 xl:bg-gray-50">
          <HotelSearchMap
            hotels={memoizedResults}
            highlightedHotelId={highlightedHotelId}
            highlightHotel={highlightHotel}
            searchLocation={
              searchVariables
                ? {
                    latitude: searchVariables.latitude,
                    longitude: searchVariables.longitude,
                    description:
                      searchVariables.description || 'Search Location',
                  }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
};

export default HotelSearch;
